/**
 * @file invoiceAdjustment.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-08
 * @description "Add credit" / "Add debit" on a sales invoice (Cam, 8 Oct): a
 *              new credit note or invoice pre-filled from the invoice, saved,
 *              linked to it, sent to Xero and — for a credit — allocated to
 *              it, in one workflow (server: services/invoiceAdjustmentService).
 *
 *   startAdjustment(onNavigate, invoiceId, kind)  hand off to Create Invoice
 *   takeAdjustment()                              what Create Invoice reads on mount
 *   adjustActionBlock(kind, invoice, access)      why the action is disabled
 *   adjustmentLines(lines)                        the invoice's lines as new lines
 *   adjustmentBanner(kind, original)              the line over the form
 *   adjustmentOutcome(result)                     what the save's answer means
 *
 * 1.1.0 (2026-10-08): the hand-off is stamped and only honoured for a minute,
 * so one left behind (Create Invoice opened for an edit instead, say) can never
 * pre-fill a later visit; and startAdjustment announces itself
 * (ADJUST_EVENT), so a Create Invoice screen that is ALREADY open — where
 * navigating to it changes nothing — picks it up at once.
 */

import { formatCurrency } from './currencyFormatter';

/** sessionStorage key Create Invoice reads on mount: JSON { invoiceId, kind, at }. */
export const ADJUST_INVOICE_KEY = 'adjustInvoice';
/** Window event startAdjustment fires, for a Create Invoice already on screen. */
export const ADJUST_EVENT = 'invoice-adjustment-handoff';
/** A hand-off older than this was left behind, not just made. */
export const HANDOFF_MAX_AGE_MS = 60 * 1000;

export const ADJUST_ACTIONS = {
  credit: { menu: 'create-credit-note', label: 'Add Credit', icon: 'remove_circle', title: 'Raise a credit note for this invoice — linked to it and allocated to it on save' },
  debit: { menu: 'create-invoice', label: 'Add Debit', icon: 'add_card', title: 'Raise an invoice for an extra charge on this invoice — linked to it on save' }
};

/** Hand "Add credit" / "Add debit" for `invoiceId` to Create Invoice. */
export function startAdjustment(onNavigate, invoiceId, kind) {
  const action = ADJUST_ACTIONS[kind];
  if (!action || typeof onNavigate !== 'function') return false;
  try {
    sessionStorage.setItem(ADJUST_INVOICE_KEY, JSON.stringify({ invoiceId: Number(invoiceId), kind, at: Date.now() }));
  } catch (_) { return false; }
  // A Create Invoice already showing does not remount when navigated to; it
  // listens for this instead (and takes the hand-off when it is its kind).
  try { window.dispatchEvent(new CustomEvent(ADJUST_EVENT, { detail: { invoiceId: Number(invoiceId), kind } })); } catch (_) { /* old browser */ }
  onNavigate(action.menu);
  return true;
}

/** The hand-off in `raw`, or null when unreadable or stale (left behind by an earlier visit). */
function parseHandoff(raw, now) {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (!ADJUST_ACTIONS[v?.kind] || !Number.isInteger(Number(v?.invoiceId))) return null;
    if (!(Number(v.at) > 0) || now - Number(v.at) > HANDOFF_MAX_AGE_MS) return null;
    return { invoiceId: Number(v.invoiceId), kind: v.kind };
  } catch (_) { return null; }
}

/** Is a fresh hand-off waiting? (Create Invoice decides whether to open the contact search.) */
export function hasPendingAdjustment(now = Date.now()) {
  try { return !!parseHandoff(sessionStorage.getItem(ADJUST_INVOICE_KEY), now); } catch (_) { return false; }
}

/** Read and clear the hand-off. null when there is none, it is unreadable, or it is stale. */
export function takeAdjustment(now = Date.now()) {
  let raw = null;
  try {
    raw = sessionStorage.getItem(ADJUST_INVOICE_KEY);
    sessionStorage.removeItem(ADJUST_INVOICE_KEY);
  } catch (_) { return null; }
  return parseHandoff(raw, now);
}

/**
 * Why the action is disabled on this row / in this window (null = enabled).
 * The document's own reason comes from the server (`adjust_block` on search
 * rows, `adjustBlock` on the detail); who is asking is decided here — the
 * same permission that creating an invoice or credit note needs.
 *
 * @param {'credit'|'debit'} kind
 * @param {object} invoice  a search row or the detail
 * @param {{ canCreate: boolean, readOnly: boolean, canNavigate?: boolean, readOnlyReason?: string }} access
 */
export function adjustActionBlock(kind, invoice, { canCreate, readOnly, canNavigate = true, readOnlyReason } = {}) {
  if (readOnly) return readOnlyReason || 'View-only access';
  if (!canCreate) return 'You do not have permission to create invoices and credit notes';
  const blocks = invoice?.adjust_block || invoice?.adjustBlock;
  if (blocks && blocks[kind]) return blocks[kind];
  if (!blocks) {
    // A row from before the server sent its reason: the one rule that needs no lookup.
    if (invoice?.is_credit || invoice?.isCredit) return 'This is a credit note — Add credit and Add debit are for invoices';
  }
  if (!canNavigate) return 'Open it from the Invoices screen to add a credit or debit';
  return null;
}

/** The invoice's lines as NEW lines: nothing points back at its stored rows or order. */
export function adjustmentLines(lines) {
  return (lines || []).map(l => ({ ...l, invoice_line_id: null, order_line_id: null }));
}

/** The banner over the form. `original` is GET /:id/adjustment/:kind's. */
export function adjustmentBanner(kind, original) {
  const no = original?.invoice_no || 'the invoice';
  if (kind === 'debit') {
    return `Debit for ${no} — change the lines to the extra charge. It will be linked to ${no} on save.`;
  }
  const due = Number(original?.amount_due) || 0;
  if (due < 0.005) {
    return `Credit for ${no} (nothing owing) — it will be linked to it on save; there is nothing left on ${no} to allocate it to.`;
  }
  return `Credit for ${no} (owing ${formatCurrency(due)}) — it will be allocated to it on save. Keep only the lines (and quantities) being credited.`;
}

/**
 * What the save's answer means for the person who pressed Save.
 * @param {object} result  POST /api/invoices answer
 * @returns {{ type: string, message: string, offer: null | { invoiceId, invoiceNo, amount } }}
 */
export function adjustmentOutcome(result) {
  const adj = result?.adjustment;
  const no = result?.invoice?.invoice_no || 'It';
  if (!adj) return { type: 'info', message: result?.xeroWarning || `${no} is saved.`, offer: null };
  const linkNote = adj.linked ? '' : ` It is not linked to ${adj.original?.invoice_no}: ${adj.linkNote || 'the link could not be recorded'}.`;
  if (adj.kind === 'debit') {
    const xero = result.xeroWarning ? ` ${result.xeroWarning}` : '';
    return {
      type: result.xeroWarning || !adj.linked ? 'warning' : 'success',
      message: `${no} is saved${adj.linked ? ` and linked to ${adj.original?.invoice_no}` : ''}.${xero}${linkNote}`,
      offer: null
    };
  }
  const a = adj.allocation || {};
  return {
    type: a.status === 'allocated' && adj.linked ? 'success' : 'warning',
    message: `${a.message || `${no} is saved.`}${linkNote}`,
    offer: a.offer || null
  };
}
